// A tiny reactive value: `get`, `set` and a hook. The interface keeps its own state (which sheet is open, big mode, the toast) in these;
// the engine's state has its own hook (engine-state.ts).

import { useEffect, useState } from 'preact/hooks';

export interface Store<T> {
  get(): T;
  set(next: T | ((value: T) => T)): void;
  /** Merge a patch into an object value. */
  patch(patch: Partial<T>): void;
  use(): T;
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<(v: T) => void>();
  const store: Store<T> = {
    get: () => value,
    set(next) {
      value = typeof next === 'function' ? (next as (v: T) => T)(value) : next;
      for (const l of listeners) l(value);
    },
    patch(patch) {
      store.set({ ...value, ...patch });
    },
    use() {
      const [v, setV] = useState(value);
      useEffect(() => {
        listeners.add(setV);
        setV(value); // a change between render and effect
        return () => { listeners.delete(setV); };
      }, []);
      return v;
    },
  };
  return store;
}
